<template>
  <div class="alerts-tab" :class="{ 'with-rail': hasRail }">
    <CountryScopeRail
      v-if="hasRail"
      :entries="railEntries"
      :selected-iso="selectedIso"
      @select="selectCountry"
    />

    <div class="alerts-body">
      <DetectionsPanel :catalog="catalog" :loading="loading" :hint="hint" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed } from 'vue'
import { fetchDetections, fetchScopes } from '@/api/ad_scans'
import { IDetectionCatalog, IDetectionScope } from '@/interfaces/ad_scans'
import DetectionsPanel from './DetectionsPanel.vue'
import CountryScopeRail from './CountryScopeRail.vue'

export default defineComponent({
  name: 'AlertsTab',
  components: { DetectionsPanel, CountryScopeRail },
  props: {
    sourceableType: {
      type: String,
      default: ''
    },
    sourceableId: {
      type: String,
      default: ''
    },
    active: {
      type: Boolean,
      default: false
    },
    catalogSource: {
      type: String as () => 'scan' | 'display',
      default: 'scan'
    }
  },
  emits: ['status'],
  setup(props, { emit }) {
    const loading = ref(false)
    const catalog = ref<IDetectionCatalog | null>(null)
    const customPresent = ref(false)
    const availableIsos = ref<string[]>([])
    const scopes = ref<IDetectionScope[]>([])
    const selectedIso = ref<string | null>(null)

    // Global has alerts only when a non-empty global override exists (scans are
    // geo-specific — the global scope has no scan fallback).
    const hasGlobalAlerts = computed(() =>
      scopes.value.some((scope) => scope.iso === null && scope.count > 0)
    )

    // The scan source (admin) has no global scope; the partner's display source
    // shows Global only when the admin actually set global alerts.
    const showGlobal = computed(
      () => props.catalogSource !== 'scan' && hasGlobalAlerts.value
    )

    const railEntries = computed(() => [
      ...(showGlobal.value ? [{ iso: null, label: 'Global' }] : []),
      ...availableIsos.value.map((iso) => ({ iso, label: iso }))
    ])

    const hasRail = computed(() => railEntries.value.length > 0)

    const hint = computed(() => {
      if (props.catalogSource === 'display' && customPresent.value) {
        return 'Alerts set by administrator.'
      }

      return 'Results from completed malware scans.'
    })

    const loadCountries = async () => {
      try {
        const result = await fetchScopes({
          sourceable_type: props.sourceableType,
          sourceable_id: props.sourceableId
        })
        availableIsos.value = result.alert_isos ?? []
        scopes.value = result.scopes ?? []
      } catch (e) {
        console.error('fetchScopes error:', e)
      }
    }

    const load = async () => {
      loading.value = true
      try {
        const result = await fetchDetections({
          sourceable_type: props.sourceableType,
          sourceable_id: props.sourceableId,
          iso: selectedIso.value
        })
        const source = result?.[props.catalogSource]
        if (source) {
          catalog.value = source
          customPresent.value = Boolean(result.custom_present)
          emit('status', result.custom_present)
        }
      } catch (e) {
        console.error('fetchDetections error:', e)
      } finally {
        loading.value = false
      }
    }

    const selectCountry = (iso: string | null) => {
      if (selectedIso.value === iso) return
      selectedIso.value = iso
      catalog.value = null
      load()
    }

    watch(
      () =>
        [
          props.active,
          props.sourceableType,
          props.sourceableId,
          props.catalogSource
        ] as const,
      async ([active]) => {
        if (!active) return

        catalog.value = null
        await loadCountries()
        // Default to Global when it carries alerts, otherwise the first country.
        selectedIso.value = showGlobal.value
          ? null
          : (availableIsos.value[0] ?? null)
        await load()
      },
      { immediate: true }
    )

    return {
      loading,
      catalog,
      hint,
      selectedIso,
      hasRail,
      railEntries,
      selectCountry
    }
  }
})
</script>

<style scoped>
.alerts-tab.with-rail {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 16px;
  min-height: 260px;
}

.alerts-body {
  min-width: 0;
}
</style>
